/**
 * DEV_IDS is a list of user IDs that are considered developers or have special privileges in the application.
 *
 * These IDs are typically used to enable development-only features.
 *
 * Example usage:
 *   if (DEV_IDS.includes(currentUserId)) {
 *     // Show developer tools
 *   }
 */
export const DEV_IDS = [
  '12345678910', // Local admin
  '12884902178', // Gustavo
  '12884901890', // Congqin
];

/**
 * The default time zone used throughout the application.
 * This is represented in the ISO 8601 format for time offsets.
 *
 * Example: '+09:00' represents a time zone that is 9 hours ahead of UTC.
 */
export const DEFAULT_TIME_ZONE = '+09:00';

/**
 * A constant representing the threshold (in seconds) for determining
 * if an event is "about to start". This value is typically used to
 * trigger notifications or updates when an event is within this time
 * frame of starting.
 */
export const THRESHOLD_FOR_ABOUT_TO_START = 3600;

/**
 * An array of supported languages, each represented by an object containing a display label and a language code.
 *
 * @remarks
 * The `label` property is the native name of the language, and the `value` property is the corresponding ISO 639-1 language code.
 *
 * @example
 * ```typescript
 * LANGUAGES.find(lang => lang.value === 'en'); // {label: 'English', value: 'en'}
 * ```
 */
export const LANGUAGES = [
  {label: '日本語', value: 'ja'}, // Japanese
  {label: 'العربية', value: 'ar'}, // Arabic
  {label: 'Deutsch', value: 'de'}, // German
  {label: 'English', value: 'en'}, // English
  {label: 'Español', value: 'es'}, // Spanish
  {label: 'Français', value: 'fr'}, // French
  {label: 'हिन्दी', value: 'hi'}, // Hindi
  {label: 'Bahasa Indonesia', value: 'id'}, // Indonesian
  {label: 'Italiano', value: 'it'}, // Italian
  {label: '한국어', value: 'ko'}, // Korean
  {label: 'Português', value: 'pt'}, // Portuguese
  {label: 'Русский', value: 'ru'}, // Russian
  {label: 'தமிழ்', value: 'ta'}, // Tamil
  {label: 'Türkçe', value: 'tr'}, // Turkish
  {label: 'ไทย', value: 'th'}, // Thai
  {label: 'Tiếng Việt', value: 'vi'}, // Vietnamese
  {label: '中文', value: 'zh'}, // Chinese
] as const;

/**
 * An array of specialty options used for setting up a public profile.
 * Each specialty consists of a `label` (used for i18n translation keys)
 * and a `value` (the display name in Japanese).
 *
 * @remarks
 * The `label` property corresponds to translation keys for internationalization,
 * while the `value` property is the Japanese name of the specialty.
 *
 * @example
 * ```typescript
 * SPECIALTIES[0] // { label: 'pages.setup-public-profile.specialty-sales', value: '営業' }
 * ```
 */
export const SPECIALTIES = [
  {label: 'pages.setup-public-profile.specialty-sales', value: '営業'},
  {
    label: 'pages.setup-public-profile.specialty-marketing',
    value: 'マーケティング',
  },
  {label: 'pages.setup-public-profile.specialty-design', value: 'デザイン'},
  {label: 'pages.setup-public-profile.specialty-planning', value: '企画'},
  {
    label: 'pages.setup-public-profile.specialty-public-relation',
    value: '広報',
  },
  {label: 'pages.setup-public-profile.specialty-finance', value: '経理'},
  {label: 'pages.setup-public-profile.specialty-accounting', value: '会計'},
  {
    label: 'pages.setup-public-profile.specialty-human-resources',
    value: '人事',
  },
  {label: 'pages.setup-public-profile.specialty-legal', value: '法務'},
  {
    label: 'pages.setup-public-profile.specialty-general-administration',
    value: '総務',
  },
  {
    label: 'pages.setup-public-profile.specialty-new-business-development',
    value: '新規事業開発',
  },
  {label: 'pages.setup-public-profile.specialty-research', value: 'リサーチ'},
  {
    label: 'pages.setup-public-profile.specialty-research-and-development',
    value: '研究開発',
  },
  {
    label: 'pages.setup-public-profile.specialty-engineering',
    value: 'エンジニアリング',
  },
  {
    label: 'pages.setup-public-profile.specialty-data-science',
    value: 'データサイエンス',
  },
  {
    label: 'pages.setup-public-profile.specialty-security',
    value: 'セキュリティ',
  },
  {
    label: 'pages.setup-public-profile.specialty-product-management',
    value: '製品管理',
  },
  {
    label: 'pages.setup-public-profile.specialty-project-management',
    value: 'プロジェクト管理',
  },
  {
    label: 'pages.setup-public-profile.specialty-program-management',
    value: 'プログラム管理',
  },
  {
    label: 'pages.setup-public-profile.specialty-customer-support',
    value: 'カスタマーサポート',
  },
] as const;
